import { Gift, Users, Heart, Tag, Trophy, ArrowRight } from "lucide-react";
import GiveawayForm from "@/components/landing/GiveawayForm";

const TELEGRAM_URL = "https://t.me/space_100xchat";

const RULES = [
    {
        icon: Users,
        text: "Follow $SPACE_100X on all 5 social platforms linked above.",
    },
    {
        icon: Heart,
        text: "Like, Comment, and Save at least one post on each of the 5 platforms.",
    },
    {
        icon: Tag,
        text: "Tag 2 friends in our latest 'Giveaway' post on X and Instagram.",
    },
];

export default function Giveaway() {
    return (
        <section
            id="giveaway"
            data-testid="giveaway-section"
            className="relative py-28 sm:py-36 px-5 sm:px-8 overflow-hidden"
        >
            {/* Background ambience */}
            <div
                aria-hidden
                className="absolute inset-0 grid-texture opacity-25 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_80%)]"
            />
            <div
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[#00F0FF]/10 blur-[160px]"
            />
            <div
                aria-hidden
                className="absolute bottom-0 right-0 w-[600px] h-[400px] rounded-full bg-[#B026FF]/15 blur-[140px]"
            />
            <div aria-hidden className="absolute inset-0 stars-bg opacity-25" />

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#A3FF12]/40 bg-[#A3FF12]/5 backdrop-blur-md px-4 py-2 mb-8">
                        <Gift size={14} className="text-[#A3FF12]" />
                        <span
                            data-testid="giveaway-eyebrow"
                            className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#A3FF12]"
                        >
                            Live Campaign · Limited Slots
                        </span>
                    </div>

                    <h2
                        data-testid="giveaway-title"
                        className="font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white"
                        style={{
                            textShadow:
                                "0 0 18px rgba(0,240,255,0.55), 0 0 40px rgba(0,240,255,0.35), 0 0 80px rgba(0,240,255,0.2)",
                        }}
                    >
                        Mission Awards:
                        <br />
                        <span className="text-[#00F0FF]">Global Giveaway</span>
                    </h2>

                    <p
                        data-testid="giveaway-announcement"
                        className="mt-6 font-mono text-sm sm:text-base text-slate-300 leading-relaxed"
                    >
                        We are rewarding our most loyal astronauts!{" "}
                        <span className="text-white">
                            Join our massive token airdrop event.
                        </span>
                    </p>
                </div>

                {/* Body grid */}
                <div className="grid lg:grid-cols-5 gap-6">
                    {/* Rules card */}
                    <div
                        data-testid="giveaway-rules-card"
                        className="lg:col-span-3 relative bg-[#0B0B1A] border border-white/10 rounded-3xl p-8 sm:p-10 overflow-hidden"
                    >
                        <div
                            aria-hidden
                            className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#A3FF12]/10 blur-3xl"
                        />
                        <div
                            aria-hidden
                            className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-[#A3FF12]/60 to-transparent"
                        />

                        <div className="relative">
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A3FF12] mb-3">
                                // The Rules
                            </div>
                            <h3 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-white">
                                Complete the mission
                            </h3>

                            <ul className="mt-8 space-y-5">
                                {RULES.map((r, i) => {
                                    const Icon = r.icon;
                                    return (
                                        <li
                                            key={i}
                                            data-testid={`giveaway-rule-${i + 1}`}
                                            className="flex items-start gap-4 group"
                                        >
                                            <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border border-[#A3FF12]/40 bg-[#A3FF12]/10 group-hover:bg-[#A3FF12]/20 transition-colors">
                                                <Icon
                                                    size={18}
                                                    className="text-[#A3FF12]"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-1">
                                                    Step 0{i + 1}
                                                </div>
                                                <p className="font-mono text-sm sm:text-base text-slate-200 leading-relaxed">
                                                    {r.text}
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                    {/* Selection criteria card */}
                    <div
                        data-testid="giveaway-criteria-card"
                        className="lg:col-span-2 relative bg-[#0B0B1A] border border-[#A3FF12]/30 rounded-3xl p-8 sm:p-10 overflow-hidden flex flex-col justify-between"
                        style={{
                            boxShadow:
                                "inset 0 0 24px rgba(163,255,18,0.06), 0 0 32px rgba(163,255,18,0.08)",
                        }}
                    >
                        <div
                            aria-hidden
                            className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-[#A3FF12]/15 blur-3xl"
                        />

                        <div className="relative">
                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#A3FF12]/15 border border-[#A3FF12]/50 mb-6">
                                <Trophy
                                    size={22}
                                    className="text-[#A3FF12]"
                                />
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A3FF12] mb-3">
                                // Selection Criteria
                            </div>
                            <h3 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-white leading-tight">
                                Random &amp; Verified
                            </h3>
                            <p
                                data-testid="giveaway-criteria-text"
                                className="mt-5 font-mono text-sm text-slate-300 leading-relaxed"
                            >
                                Winners will be randomly selected from those
                                who complete all objectives.{" "}
                                <span className="text-white">
                                    Proof of action will be verified.
                                </span>{" "}
                                Results announced live on Telegram!
                            </p>
                        </div>

                        {/* CTA buttons */}
                        <div className="relative mt-10 flex flex-col gap-3">
                            <a
                                href={TELEGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="giveaway-join-button"
                                className="group inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full bg-[#A3FF12] hover:bg-[#A3FF12]/90 text-black font-display font-black uppercase tracking-tight text-sm transition-all hover:-translate-y-0.5"
                                style={{
                                    boxShadow:
                                        "0 0 28px rgba(163,255,18,0.55), 0 0 60px rgba(163,255,18,0.25)",
                                }}
                            >
                                Join the Giveaway
                                <ArrowRight
                                    size={18}
                                    className="group-hover:translate-x-1 transition-transform"
                                />
                            </a>
                            <a
                                href="#mission-control"
                                data-testid="giveaway-socials-link"
                                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-[#FF7A1A]/50 bg-[#FF7A1A]/10 text-[#FF7A1A] hover:bg-[#FF7A1A]/20 hover:border-[#FF7A1A] font-mono text-xs uppercase tracking-[0.25em] transition-colors"
                            >
                                View Social Platforms
                            </a>
                        </div>
                    </div>
                </div>

                {/* Entry form */}
                <div className="mt-10">
                    <GiveawayForm />
                </div>
            </div>
        </section>
    );
}
